// RelationshipsPanel — "every relationship it carries, one click to any of
// them" (PRD §10; three-tier client model Stage 4, ADR-0015 D7). Embedded on
// all three detail pages (Contact / Suitability Record / Client): both
// directions merged server-side, counterpart display name + role labels
// (the Child Minor/Adult label is DOB-derived server-side — the stored role
// is not authoritative), ended relationships greyed and marked "Ended" —
// never hidden. Desk/Ops get the add / end / delete affordances via
// `editable`; Comply renders read-only.
//
// END AND DELETE ARE DIFFERENT ACTIONS (SEP-244.4). End sets an end date, and
// the row stays as history. Delete removes the row, and the firm keeps no copy
// of it. `RelationshipDeleteDialog` says that difference before the delete
// runs, so a person who wanted End does not press Delete. Delete applies to a
// current row AND to an ended one, because a wrong link is often found after
// someone ended it. Edit and End stay on a current row only.
//
// ONE PANEL FOR ONE DIRECTION (SEP-227). Each page now mounts this component
// once for each counterpart tier, and hands it a `title`, an `includeRow`
// filter and the single tier its add form may pick from. The rules that decide
// the panels and the rows are pure, and live in `relationship-panels.ts`.
//
// NO DATE COLUMNS. The Start and End dates left the table on a product
// decision: the reader wants the endpoints and the roles, and an end date told
// them nothing the greyed row and its "Ended" marker do not. The marker stays,
// because an ended link that reads as an active one is worse than no column.
// The book-level register (`BookRelationshipsDirectory`) keeps both dates: it
// is the audit surface, and this is the working one.
//
// Nominated Representative warnings (PRD §9.3) ride the create response and
// render as an amber notice — warn, never block.
//
// A SINGLE-ROLE KIND (`isSingleRoleRelationshipKind`): the role
// describes the CONTACT's role on the record/client; the other end mirrors
// the same key by storage convention and is never displayed (the SUR must
// not render "Nominated Representative" about itself). Documented in
// vendor/septima-ontology-wealth/packages/ontology-wealth/specs/client/relationship.md.
//
// DERIVED ROWS (SEP-188): a row with a `via` is INFERRED one hop through a
// suitability record — a contact reaches the client its record owns, and the
// client reaches the contact, though neither link is recorded. The server
// derives it on read; the panel adds no reader of its own. Two rules follow.
// The Party 2 cell names the record the link runs through, so an inferred link
// never reads as a recorded one. And the row carries NO End affordance: there
// is no relationship row to end, only its two legs.

import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { usePathname } from '@/lib/navigation';
import { clientHref, contactHref, suitabilityRecordHref } from '@/lib/tri-tier-links';
import { Button, DataTable, RowActions, Select } from '@/components/ui';
import type { DataTableColumn } from '@/components/ui';
import EmptyState from '@/components/EmptyState';
import {
    clientKindLabel,
    contactPickerLabel,
    relationshipPairKind,
    RELATIONSHIP_ENDPOINT_TYPE_LABELS as ENDPOINT_TYPE_LABELS,
    isSingleRoleRelationshipKind,
    searchTokens,
    SUITABILITY_RECORD_LABEL_SEPARATOR,
    suitabilityRecordMatchesSearch,
    suitabilityRecordPickerLabel,
    type RelationshipEndpointType,
    type RelationshipRoleDto,
    type RelationshipRow,
} from '@septima/contract';
import {
    useCreateRelationship,
    useDeleteRelationship,
    useEndRelationship,
    useRelationshipRoles,
    useRelationships,
} from '@/lib/relationships/hooks';
import RelationshipDeleteDialog from '@/components/relationships/RelationshipDeleteDialog';
import RelationshipEditForm, {
    type RelationshipEditEndpoint,
} from '@/components/relationships/RelationshipEditForm';
import {
    endpoint2SortValue,
    role1SortValue,
    role2SortValue,
    roleVisibility,
} from '@/components/relationships/relationship-panels';
import { useContacts } from '@/lib/contacts/hooks';
import { useSuitabilityRecords } from '@/lib/suitability-records/hooks';
import { useAccounts, useClientSearch } from '@/lib/clients/hooks';

export interface RelationshipsPanelProps {
    endpointType: RelationshipEndpointType;
    endpointId: string;
    /** The current record's display name — Party 1 in the Titan table. */
    endpointLabel: string;
    /** Desk / Ops — add + end affordances; Comply renders read-only. */
    editable?: boolean;
    /** The panel heading. One page stacks several panels, so each one says
     *  which direction it holds (SEP-227). */
    title?: string;
    /** Which rows this panel shows. A page passes `rowBelongsToPanel`; a caller
     *  that mounts one panel for everything passes nothing. */
    includeRow?: (row: RelationshipRow) => boolean;
    /** The tiers the add form may pick from. With one tier permitted the tier
     *  select disappears, because a chooser with one choice is furniture. */
    allowedCounterpartTypes?: readonly RelationshipEndpointType[];
    /** Whether this panel offers an Add button at all — a separate question
     *  from `editable`, which is about the reader's persona. */
    allowAdd?: boolean;
    /** The empty state, in the words of this panel's direction. */
    emptyTitle?: string;
    /** Names the panel's add button for a scenario step (`{testid=…}`). Unset
     *  on a single-panel caller, where the label is already unambiguous. */
    panelKey?: string;
}

/** What each tier's type-ahead actually matches on — the record tier names its
 *  extra tokens because "the SUR only shows its own code" was the complaint. */
const SEARCH_PLACEHOLDERS: Record<RelationshipEndpointType, string> = {
    contact: 'Search contacts…',
    'suitability-record': 'Search by name, reference or client…',
    account: 'Search accounts…',
    client: 'Search clients…',
};

/**
 * Counterpart types a given endpoint type may link to (ADR-0015 D7).
 *
 * THE LIST IS NOW THE WHOLE ENDPOINT ENUM. `'client'` joined it together with
 * its type-ahead (`searchClients`): the tier was held back only because a
 * counterpart a reader cannot search for is worse than no counterpart at all,
 * and that reason is gone. `relationshipPairKind` still decides which of the
 * four an endpoint may reach, so a pair the model does not name is dropped
 * here and never reaches a select.
 */
function counterpartTypesFor(endpointType: RelationshipEndpointType): RelationshipEndpointType[] {
    const all: RelationshipEndpointType[] = ['contact', 'suitability-record', 'account', 'client'];
    return all.filter((candidate) => relationshipPairKind(endpointType, candidate) !== null);
}

/** Route for a counterpart row under the current view, or null when there is
 * nowhere to land. A `account` counterpart opens its person Client's
 * Client Book record (SEP-188) — the Clients tier has no detail page of its
 * own — so it links on the server-resolved `counterpartClientId`, never on the
 * Account id; that is null for a joint Account or one whose client sits
 * outside the caller's read, and those stay plain text. */
function counterpartHref(view: string, row: RelationshipRow): string | null {
    // `tri-tier-links` is the single home of "which tier can this view open" —
    // View exposes NEITHER sibling page. The panel became reachable from View
    // with the Client record's Relationships tab (SEP-177), so without that
    // rule every counterpart there would link to a route that does not exist.
    // The Client Book link is the exception: every view has that route.
    if (row.counterpartType === 'contact') return contactHref(view, row.counterpartId);
    if (row.counterpartType === 'suitability-record') {
        return suitabilityRecordHref(view, row.counterpartId);
    }
    // A `client` counterpart IS the Client, so its own id is the route key. An
    // Account counterpart borrows its person Client's id, which the server
    // resolves onto `counterpartClientId`.
    if (row.counterpartType === 'client') return clientHref(view, row.counterpartId);
    return clientHref(view, row.counterpartClientId);
}

/**
 * The counterpart cell's second line: the tier word, plus the Account's own
 * reference when it carries one (`Client · 2620 140229`). The Clients tier
 * leads with the person (SEP-188), so its reference reads here — it is what
 * tells two Accounts of one person apart. The book register's twin of this
 * rule lives in `BookRelationshipsDirectory`.
 */
function counterpartTierAnnotation(row: RelationshipRow): string {
    const tier = ENDPOINT_TYPE_LABELS[row.counterpartType];
    return row.counterpartReference
        ? `${tier}${SUITABILITY_RECORD_LABEL_SEPARATOR}${row.counterpartReference}`
        : tier;
}

// `roleVisibility` moved to `relationship-panels.ts`, beside the sort keys that
// read it — the rule and its consequences now have one home.

const dash = <span className="text-[rgb(var(--color-text-tertiary))]">—</span>;

/**
 * The panel's perspective row, turned back into the CANONICAL pair the edit
 * form and `update-relationship` take (SEP-214).
 *
 * The panel writes every row from the current record's point of view, which is
 * what a reader wants and what an editor cannot use: the Action takes
 * `endpoint1Role` / `endpoint2Role` in stored order. `ownIsEndpoint1` is the server's
 * answer, and it matters most for contact↔contact links, where the two roles
 * differ and a guess would swap them.
 *
 * Each end seeds from `storedKey`, never from the displayed `key` — an
 * age-banded role shows its DOB-derived label, and correcting a note must not
 * settle that derivation into the row.
 */
function canonicalPair(
    row: RelationshipRow,
    endpointType: RelationshipEndpointType,
    endpointLabel: string,
): { endpoint1: RelationshipEditEndpoint; endpoint2: RelationshipEditEndpoint } | null {
    if (row.ownIsEndpoint1 === null) return null;
    const own: RelationshipEditEndpoint = {
        endpointType,
        label: endpointLabel,
        roleKey: row.ownRole.storedKey,
    };
    const counterpart: RelationshipEditEndpoint = {
        endpointType: row.counterpartType,
        label: row.counterpartName,
        roleKey: row.counterpartRole.storedKey,
    };
    return row.ownIsEndpoint1
        ? { endpoint1: own, endpoint2: counterpart }
        : { endpoint1: counterpart, endpoint2: own };
}

/**
 * The marker on a DERIVED row (SEP-188): the suitability record the inferred
 * link runs through, under the counterpart's tier label. It renders nothing at
 * all on a stored row, so the presence of the line IS the distinction.
 *
 * The record itself is a link where the view has a Suitability Records route —
 * View has none (`tri-tier-links`), so it reads as plain text there rather than
 * pointing at a page that does not exist.
 */
function ViaMarker({ view, row }: { view: string; row: RelationshipRow }) {
    if (!row.via) return null;
    const href = row.via.name ? suitabilityRecordHref(view, row.via.endpointId) : null;
    const name = row.via.name ?? 'a record not in your book';
    return (
        <p className="text-annotation text-[rgb(var(--color-text-tertiary))]">
            via{' '}
            {href ? (
                <Link to={href} className="text-[rgb(var(--color-accent))] hover:underline">
                    {name}
                </Link>
            ) : (
                name
            )}
        </p>
    );
}

/**
 * The options of one tier for a search, through the SAME book-scoped hooks
 * that the picker below uses (see its note on scope). EXPORTED so the Client
 * Book "Complete client links" dialog (SEP-300) reads the same options and can
 * count the matches: no match and several matches each need a message there.
 */
export function useCounterpartOptions(
    type: RelationshipEndpointType,
    search: string,
    enabled = true,
): { options: Array<{ id: string; label: string }>; isLoading: boolean } {
    // Only the selected tier fetches — the other three hooks stay disabled.
    const contacts = useContacts(
        { search: search || undefined, contactKind: 'person' },
        { enabled: enabled && type === 'contact' },
    );
    // The record tier fetches ONCE, unfiltered, and narrows in the browser
    // (SEP-180). Re-querying per keystroke changes the react-query key, so
    // `data` goes undefined mid-flight and the options blink empty under the
    // cursor — half of why the picker read as unusable. The caller's own book
    // of records is small enough to hold, exactly as the Contacts directory
    // holds its rows, and the filter below is the same shared predicate the
    // server would have applied.
    const records = useSuitabilityRecords(undefined, {
        enabled: enabled && type === 'suitability-record',
    });
    const accounts = useAccounts(
        { search: search || undefined },
        { enabled: enabled && type === 'account' },
    );
    // The registered-holder tier searches SERVER-side, like the Account tier
    // above it and unlike the record one: the tier is far too large to hold in
    // the browser, and the server caps the list it returns.
    const clients = useClientSearch(
        { search: search || undefined },
        { enabled: enabled && type === 'client' },
    );

    const options: Array<{ id: string; label: string }> = useMemo(() => {
        if (type === 'contact') {
            // The label carries the status and the last four digits of a
            // number, not the name alone (SEP-215). Production holds 2,077
            // Contacts and every one of them is `draft`, so a column of bare
            // names tells two candidates apart for nobody.
            return (contacts.data?.rows ?? [])
                .filter((row) => row.contactKind === 'person')
                .map((row) => ({ id: row.contactId, label: contactPickerLabel(row) }));
        }
        if (type === 'suitability-record') {
            // A record's own code says nothing to the person picking it —
            // `entityName` is set on entity records only, so 742 of 901
            // options used to read `SUR-1234` and nothing else (SEP-180).
            // `suitabilityRecordPickerLabel` adds the human anchor and the
            // linked account references; the type-ahead matches every token
            // the label shows, not just the reference.
            const tokens = searchTokens(search);
            return (records.data?.rows ?? [])
                .filter((row) => suitabilityRecordMatchesSearch(row, tokens))
                .map((row) => ({
                    id: row.recordId,
                    label: suitabilityRecordPickerLabel(row),
                }));
        }
        if (type === 'client') {
            // The kind tells two holders of one name apart — a person and the
            // trust that carries their surname. `clientKindLabel` is the shared
            // map, so the picker cannot disagree with the client record about
            // what a kind is called; a row with no kind shows the bare name.
            return (clients.data?.rows ?? []).map((row) => {
                const kind = clientKindLabel(row.clientKind);
                return { id: row.clientId, label: kind ? `${row.name} — ${kind}` : row.name };
            });
        }
        return (accounts.data?.rows ?? []).map((row) => {
            const ref = row.displayReference ?? row.accountId;
            return {
                id: row.accountId,
                label: row.clientName ? `${ref} — ${row.clientName}` : ref,
            };
        });
    }, [type, search, contacts.data, records.data, accounts.data, clients.data]);

    const active =
        type === 'contact'
            ? contacts
            : type === 'suitability-record'
              ? records
              : type === 'client'
                ? clients
                : accounts;
    return { options, isLoading: enabled && active.isLoading };
}

/**
 * Search-driven counterpart picker for one tier.
 *
 * THE OPTION SET IS BOOK-SCOPED BY CONSTRUCTION, and that is deliberate: the
 * picker reads through the SAME three hooks the three directories use, so the
 * rows it offers are exactly the rows the caller may already see. Scope comes
 * from each Object Type's `deskRowScope` (`Contact`/`SuitabilityRecord`
 * `book-owner`, `Account` `book-account` — ADR-0005), never from a
 * parameter this component passes. Do not swap in a wider endpoint "so the
 * list is complete"; pinned by
 * `vendor/septima-ontology-wealth/packages/ontology-wealth/src/__tests__/picker-tier-scope.test.ts`.
 *
 * EXPORTED because the Client Knowledge graph and the relationship-proposal
 * review both pick a counterpart, and a second picker would be a second answer
 * to "what may this reader see" (SEP-215).
 */
export function CounterpartPicker({
    type,
    value,
    onPick,
    searchPlaceholder,
    searchLabel,
}: {
    type: RelationshipEndpointType;
    value: string;
    onPick: (id: string) => void;
    /** SEP-215: the Client Knowledge graph names its search box in the firm's
     *  own words ("Search the contact book"). Every other surface leaves both
     *  unset and keeps the tier default, which is what the scenarios that fill
     *  a "Search contacts" field read. */
    searchPlaceholder?: string;
    searchLabel?: string;
}) {
    const [search, setSearch] = useState('');
    const { options } = useCounterpartOptions(type, search);

    return (
        <div className="flex items-center gap-2 flex-wrap">
            <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={searchPlaceholder ?? SEARCH_PLACEHOLDERS[type]}
                aria-label={searchLabel ?? `Search ${ENDPOINT_TYPE_LABELS[type].toLowerCase()}s`}
                // The box defaults to 170px, and every placeholder here is
                // longer than that: the record tier's has always been cut off
                // mid-word, and "Search the contact book…" lost its ellipsis.
                className="h-8 px-2 min-w-[14rem] rounded-button border border-[rgb(var(--color-divider))] bg-[rgb(var(--color-surface-primary))] text-body text-[rgb(var(--color-text-primary))]"
            />
            <Select
                size="sm"
                value={value}
                onChange={(e) => onPick(e.target.value)}
                aria-label="Pick counterpart"
            >
                <option value="">Select…</option>
                {options.map((option) => (
                    <option key={option.id} value={option.id}>
                        {option.label}
                    </option>
                ))}
            </Select>
        </div>
    );
}

/**
 * The add form, EXPORTED so the Client Knowledge graph writes a relationship
 * through this one path (SEP-215). The graph used to post its own
 * `add-client-connection` body to an endpoint that is now retired.
 *
 * `allowedCounterpartTypes` narrows the tier select. The graph draws people
 * only, so it passes `['contact']` and the select then renders nothing: a
 * chooser with one choice is furniture.
 */
export function AddRelationshipForm({
    endpointType,
    endpointId,
    roles,
    allowedCounterpartTypes,
    counterpartLabel,
    counterpartSearchPlaceholder,
    counterpartSearchLabel,
    onDone,
}: {
    endpointType: RelationshipEndpointType;
    endpointId: string;
    roles: readonly RelationshipRoleDto[];
    allowedCounterpartTypes?: readonly RelationshipEndpointType[];
    /** SEP-215: a visible name for the counterpart box, and the words its search
     *  field carries. The panel sets none of the three, because there the box is
     *  one row of a full form. The Client Knowledge modal sets all three, because
     *  there the box IS the modal and the firm named it. */
    counterpartLabel?: string;
    counterpartSearchPlaceholder?: string;
    counterpartSearchLabel?: string;
    onDone: (warnings: string[]) => void;
}) {
    const permitted = counterpartTypesFor(endpointType);
    const counterpartTypes = allowedCounterpartTypes
        ? permitted.filter((type) => allowedCounterpartTypes.includes(type))
        : permitted;
    const [counterpartType, setCounterpartType] = useState<RelationshipEndpointType>(
        counterpartTypes[0],
    );
    const [counterpartId, setCounterpartId] = useState('');
    const [counterpartRole, setCounterpartRole] = useState('');
    const [ownRole, setOwnRole] = useState('');
    // The start date left the form on the same product decision that took the
    // date columns off the table (SEP-227). The create contract keeps
    // `startDate` optional, so the form simply sends none.
    const [note, setNote] = useState('');
    const create = useCreateRelationship();

    const kind = relationshipPairKind(endpointType, counterpartType);
    const kindRoles = roles.filter((role) => role.active && role.appliesTo === kind);
    // Single-role kinds: one select — the contact's role on the record /
    // client; the other end mirrors the same key by storage convention.
    const singleRoleKind = kind !== null && isSingleRoleRelationshipKind(kind);

    const effectiveOwnRole = singleRoleKind ? counterpartRole : ownRole;
    const canSubmit = Boolean(
        counterpartId && counterpartRole && effectiveOwnRole && !create.isPending,
    );

    return (
        <div className="mt-2 flex flex-col gap-2 rounded-card border border-[rgb(var(--color-divider))] p-3">
            <div className="flex flex-col gap-1">
                {counterpartLabel && (
                    <span className="text-annotation font-medium text-[rgb(var(--color-text-primary))]">
                        {counterpartLabel}
                    </span>
                )}
                <div className="flex items-center gap-2 flex-wrap">
                    {counterpartTypes.length > 1 && (
                        <Select
                            size="sm"
                            value={counterpartType}
                            aria-label="Counterpart type"
                            onChange={(e) => {
                                setCounterpartType(e.target.value as RelationshipEndpointType);
                                setCounterpartId('');
                                setCounterpartRole('');
                                setOwnRole('');
                            }}
                        >
                            {counterpartTypes.map((type) => (
                                <option key={type} value={type}>
                                    {ENDPOINT_TYPE_LABELS[type]}
                                </option>
                            ))}
                        </Select>
                    )}
                    <CounterpartPicker
                        type={counterpartType}
                        value={counterpartId}
                        onPick={setCounterpartId}
                        searchPlaceholder={counterpartSearchPlaceholder}
                        searchLabel={counterpartSearchLabel}
                    />
                </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
                <span className="text-annotation text-[rgb(var(--color-text-tertiary))]">
                    {singleRoleKind ? 'Role' : 'Their role'}
                </span>
                <Select
                    size="sm"
                    value={counterpartRole}
                    aria-label="Counterpart role"
                    onChange={(e) => {
                        setCounterpartRole(e.target.value);
                        // Symmetric default: mirror the pick until the user
                        // chooses this record's own role explicitly.
                        if (!ownRole) setOwnRole(e.target.value);
                    }}
                >
                    <option value="">Select role…</option>
                    {kindRoles.map((role) => (
                        <option key={role.key} value={role.key}>
                            {role.label}
                        </option>
                    ))}
                </Select>
                {!singleRoleKind && (
                    <>
                        <span className="text-annotation text-[rgb(var(--color-text-tertiary))]">
                            This record&apos;s role
                        </span>
                        <Select
                            size="sm"
                            value={ownRole}
                            aria-label="Own role"
                            onChange={(e) => setOwnRole(e.target.value)}
                        >
                            <option value="">Select role…</option>
                            {kindRoles.map((role) => (
                                <option key={role.key} value={role.key}>
                                    {role.label}
                                </option>
                            ))}
                        </Select>
                    </>
                )}
                <input
                    type="text"
                    value={note}
                    aria-label="Note"
                    placeholder="Note (optional)"
                    onChange={(e) => setNote(e.target.value)}
                    className="h-8 px-2 rounded-button border border-[rgb(var(--color-divider))] bg-[rgb(var(--color-surface-primary))] text-body text-[rgb(var(--color-text-primary))]"
                />
                <Button
                    variant="primary"
                    size="sm"
                    busy={create.isPending}
                    disabled={!canSubmit}
                    onClick={() =>
                        void create
                            .mutateAsync({
                                endpoint1Type: endpointType,
                                endpoint1Id: endpointId,
                                endpoint1Role: effectiveOwnRole,
                                endpoint2Type: counterpartType,
                                endpoint2Id: counterpartId,
                                endpoint2Role: counterpartRole,
                                note: note.trim() || undefined,
                            })
                            .then((outcome) => onDone(outcome.warnings))
                            // Failure surfaces via create.error below; catch
                            // so the rejection never escapes unhandled.
                            .catch(() => undefined)
                    }
                >
                    Add relationship
                </Button>
            </div>
            {create.error instanceof Error && (
                <p className="text-annotation text-[rgb(var(--color-rag-red))]">
                    {create.error.message}
                </p>
            )}
        </div>
    );
}

export function RelationshipsPanel({
    endpointType,
    endpointId,
    endpointLabel,
    editable = false,
    title = 'Relationships',
    includeRow,
    allowedCounterpartTypes,
    allowAdd = true,
    emptyTitle = 'No relationships recorded',
    panelKey,
}: RelationshipsPanelProps) {
    const pathname = usePathname();
    const view = pathname.split('/')[1] ?? 'desk';
    // One react-query key for one endpoint, so the panels of one page share ONE
    // fetch however many of them the page stacks. Each panel then keeps its own
    // rows in the browser.
    const { data, isLoading } = useRelationships(endpointId);
    const { data: rolesData } = useRelationshipRoles();
    const end = useEndRelationship();
    const remove = useDeleteRelationship();
    const [adding, setAdding] = useState(false);
    const [endingId, setEndingId] = useState<string | null>(null);
    const [editingId, setEditingId] = useState<string | null>(null);
    // The row the delete dialog asks about, and the reason the last try failed.
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [deleteError, setDeleteError] = useState<string | null>(null);
    const [warnings, setWarnings] = useState<string[]>([]);
    const canAdd = editable && allowAdd;

    const allRows = data?.rows ?? [];
    const rows = includeRow ? allRows.filter(includeRow) : allRows;
    const editing = rows.find((row) => row.relationshipId === editingId) ?? null;
    const editingPair = editing ? canonicalPair(editing, endpointType, endpointLabel) : null;

    const endRelationship = (relationshipId: string) => {
        setEndingId(relationshipId);
        // Failure surfaces via end.error below; catch so the rejection never
        // escapes unhandled.
        void end.mutateAsync(relationshipId).catch(() => undefined);
    };

    // The row the delete dialog names, and the two ends it says out loud.
    const deleting = rows.find((row) => row.relationshipId === deletingId) ?? null;
    const deletingSubject = deleting
        ? `${endpointLabel} and ${deleting.counterpartName ?? 'the other party'}`
        : null;

    const deleteRelationship = () => {
        if (deletingId === null) return;
        setDeleteError(null);
        void remove
            .mutateAsync(deletingId)
            .then(() => setDeletingId(null))
            .catch((err: unknown) =>
                setDeleteError(err instanceof Error ? err.message : 'The delete failed.'),
            );
    };

    // Titan-CRM two-endpoint layout (PRD §9.1): Party 1 is always the current
    // record; Role 1 / Role 2 are the two role slots; Party 2 is the linked
    // counterpart (one click through). No description column.
    //
    // All four columns sort A-Z (SEP-227), on the same keys the book-level
    // register uses. Party 1 holds one value for the whole table, so its toggle
    // reorders nothing — it is there because a reader who finds three of four
    // headers clickable reads the fourth as broken. There is no `initialSort`:
    // the server order (active links first) is what the page opens on.
    const columns = useMemo<DataTableColumn<RelationshipRow>[]>(() => {
        return [
            {
                id: 'endpoint1',
                header: 'Party 1',
                minWidth: '180px',
                hideable: false,
                sortable: true,
                sortValue: () => endpointLabel,
                // Every row holds the same Party 1, so a filter has nothing to pick.
                filterable: false,
                cell: () => (
                    <div className="min-w-0">
                        <p className="text-table-body text-[rgb(var(--color-text-primary))] truncate">
                            {endpointLabel}
                        </p>
                        <p className="text-annotation text-[rgb(var(--color-text-tertiary))]">
                            {ENDPOINT_TYPE_LABELS[endpointType]}
                        </p>
                    </div>
                ),
            },
            {
                id: 'role1',
                header: 'Role 1',
                minWidth: '140px',
                sortable: true,
                sortValue: role1SortValue,
                cell: (row) => (roleVisibility(row).showOwn ? row.ownRole.label : dash),
            },
            {
                id: 'role2',
                header: 'Role 2',
                minWidth: '140px',
                sortable: true,
                sortValue: role2SortValue,
                cell: (row) =>
                    roleVisibility(row).showCounterpart ? row.counterpartRole.label : dash,
            },
            {
                id: 'endpoint2',
                header: 'Party 2',
                minWidth: '180px',
                sortable: true,
                sortValue: endpoint2SortValue,
                cell: (row) => {
                    const href = counterpartHref(view, row);
                    const name = row.counterpartName ?? 'Not in your book';
                    return (
                        <div className="min-w-0">
                            {href && row.counterpartName ? (
                                <Link
                                    to={href}
                                    className="text-table-body text-[rgb(var(--color-accent))] hover:underline truncate"
                                >
                                    {name}
                                </Link>
                            ) : (
                                <p className="text-table-body text-[rgb(var(--color-text-primary))] truncate">
                                    {name}
                                </p>
                            )}
                            <p className="text-annotation text-[rgb(var(--color-text-tertiary))] truncate">
                                {counterpartTierAnnotation(row)}
                            </p>
                            <ViaMarker view={view} row={row} />
                            {/* An ended link stays visible and greyed — never
                                deleted (PRD §9.1). The date column is gone, so
                                the word carries the status on its own. */}
                            {row.endDate && (
                                <p className="text-annotation text-[rgb(var(--color-text-tertiary))]">
                                    Ended
                                </p>
                            )}
                        </div>
                    );
                },
            },
            {
                id: 'reference',
                header: 'Reference',
                minWidth: '120px',
                defaultHidden: true,
                sortable: true,
                sortValue: (row) => row.counterpartReference,
                cell: (row) => (
                    <span className="font-mono tabular-nums">
                        {row.counterpartReference ?? dash}
                    </span>
                ),
            },
            {
                id: 'note',
                header: 'Note',
                minWidth: '200px',
                defaultHidden: true,
                sortable: true,
                sortValue: (row) => row.note ?? null,
                filterValue: (row) => row.note,
                cell: (row) => row.note ?? dash,
            },
            ...(editable
                ? [
                      {
                          id: 'actions',
                          header: '',
                          minWidth: '196px',
                          align: 'right' as const,
                          // A DERIVED row has NO affordance at all (SEP-188):
                          // there is no relationship row behind its `derived:`
                          // id, so the request could only 404. You end,
                          // correct or delete one of its two legs, on the
                          // record they are recorded on.
                          //
                          // An ENDED row keeps DELETE and loses the other two
                          // (SEP-244.4). `update-relationship` and
                          // `end-relationship` both refuse an ended row, so
                          // Edit and End could only produce a
                          // RELATIONSHIP_ENDED error. `delete-relationship`
                          // carries no precondition on `endDate`, and a wrong
                          // link is often found after someone ended it.
                          cell: (row: RelationshipRow) =>
                              row.via ? null : (
                                  <RowActions className="justify-end">
                                      {!row.endDate && (
                                          <Button
                                              variant="secondary"
                                              size="sm"
                                              onClick={() => {
                                                  setEditingId(row.relationshipId);
                                                  setAdding(false);
                                                  setWarnings([]);
                                              }}
                                          >
                                              Edit
                                          </Button>
                                      )}
                                      {!row.endDate && (
                                          <Button
                                              variant="secondary"
                                              size="sm"
                                              busy={
                                                  end.isPending && endingId === row.relationshipId
                                              }
                                              disabled={
                                                  end.isPending && endingId === row.relationshipId
                                              }
                                              onClick={() => endRelationship(row.relationshipId)}
                                          >
                                              End
                                          </Button>
                                      )}
                                      {/* End and Delete are different actions.
                                          End keeps the row as history; Delete
                                          removes it and keeps no copy. The
                                          dialog says that difference before
                                          anything happens. Every row carries
                                          this label, so the testid names the
                                          control and the scenario scopes it to
                                          a row. */}
                                      <Button
                                          variant="danger"
                                          size="sm"
                                          data-testid="relationship-delete"
                                          onClick={() => {
                                              setDeleteError(null);
                                              setDeletingId(row.relationshipId);
                                          }}
                                      >
                                          Delete
                                      </Button>
                                  </RowActions>
                              ),
                      },
                  ]
                : []),
        ];
        // endRelationship / end are stable enough for render; deps kept minimal.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [endpointLabel, endpointType, view, editable, end.isPending, endingId]);

    return (
        <section className="rounded-card border border-[rgb(var(--color-divider))] bg-[rgb(var(--color-surface-primary))] p-4">
            <div className="flex items-center justify-between gap-3 mb-3">
                <h2 className="text-section-title text-[rgb(var(--color-text-primary))]">
                    {title}
                </h2>
                {canAdd && (
                    <Button
                        variant="secondary"
                        size="sm"
                        // Several panels carry a button with this label, so the
                        // label alone cannot name one. The testid does.
                        data-testid={panelKey ? `relationships-${panelKey}-add` : undefined}
                        onClick={() => {
                            setAdding((open) => !open);
                            // One form at a time — two open at once read as one
                            // form with twice the fields.
                            setEditingId(null);
                            setWarnings([]);
                        }}
                    >
                        {adding ? 'Cancel' : 'Add relationship'}
                    </Button>
                )}
            </div>

            {/* PRD §9.3 — warn, never block. */}
            {warnings.length > 0 && (
                <div className="mb-3 rounded-card border border-[rgb(var(--color-rag-amber))] bg-[rgb(var(--color-rag-amber)/0.08)] px-3 py-2">
                    {warnings.map((warning) => (
                        <p
                            key={warning}
                            className="text-annotation text-[rgb(var(--color-text-primary))]"
                        >
                            {warning}
                        </p>
                    ))}
                </div>
            )}

            {canAdd && adding && (
                <div className="mb-3">
                    <AddRelationshipForm
                        endpointType={endpointType}
                        endpointId={endpointId}
                        roles={rolesData?.roles ?? []}
                        allowedCounterpartTypes={allowedCounterpartTypes}
                        onDone={(newWarnings) => {
                            setAdding(false);
                            setWarnings(newWarnings);
                        }}
                    />
                </div>
            )}

            {/* Inline rather than in a Modal, so the correction sits with the
                row it corrects — and so a scenario can assert on it, which a
                portal mounted outside <main> does not allow. `editingPair` is
                null for a derived row, which has no canonical order to edit. */}
            {editable && editing && editingPair && (
                <div className="mb-3">
                    <RelationshipEditForm
                        key={editing.relationshipId}
                        relationshipId={editing.relationshipId}
                        kind={editing.kind}
                        endpoint1={editingPair.endpoint1}
                        endpoint2={editingPair.endpoint2}
                        note={editing.note}
                        roles={rolesData?.roles ?? []}
                        onCancel={() => setEditingId(null)}
                        onSaved={(newWarnings) => {
                            setEditingId(null);
                            setWarnings(newWarnings);
                        }}
                    />
                </div>
            )}

            <DataTable<RelationshipRow>
                ariaLabel={title}
                columns={columns}
                data={rows}
                rowKey={(row) => row.relationshipId}
                rowClassName={(row) => (row.endDate ? 'opacity-50' : undefined)}
                loading={isLoading}
                empty={<EmptyState title={emptyTitle} />}
            />
            {end.error instanceof Error && (
                <p className="mt-2 text-annotation text-[rgb(var(--color-rag-red))]">
                    {end.error.message}
                </p>
            )}

            <RelationshipDeleteDialog
                open={deleting !== null}
                subject={deletingSubject}
                ended={deleting?.endDate != null}
                busy={remove.isPending}
                error={deleteError}
                onClose={() => setDeletingId(null)}
                onConfirm={deleteRelationship}
            />
        </section>
    );
}
